(ns manage.ad-variant.creation.content.columns
  "MUI DataGrid columns for content columns

  For ad-variant implementation, but the render fns
  could be useful elsewhere."
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/Link$default" :as Link]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium" :refer [GridActionsCellItem]]
            [cljs-bean.core :refer [->clj ->js]]
            [common.queries :refer [use-query]]
            [common.util :as util]
            [common.widgets.column-renderers :refer [template-dimensions-renderer]]
            [goog.object :as gobject]
            [manage.ad-variant.queries :as q]
            [manage.content-variant-page.table.db :refer [class->filter-resolver]]
            [mate.core :as mc]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

;; DataGrid wants FUNCTIONS that return ReactElements,
;; so these aren't components.
;; It would be possible to use UIx functions from a fn,
;; and even convert the JS params to Cljs with cljs-bean,
;; but the extra layers would make this a bit slower.

(defn render-link-value [^js params]
  ($ Link
     {:color "inherit"
      :underline "hover"
      :no-wrap true
      :href (.-value params)
      :target "_blank"}
     (.-value params)))

(defn render-audio-preview [^js params]
  ($ Box
     {:component "audio"
      :controls true
      :src (.-value params)
      :sx #js {:width "100%"
               :height "100%"}}))

(defn render-image-preview [^js params]
  ($ Box
     {:component "a"
      :sx #js {:py 0.5
               :height "100%"}
      :href (.-value params)
      :target "_blank"}
     ($ Box
        {:component "img"
         :sx #js {:cursor "pointer"
                  :maxWidth "100%"
                  :height "100%"
                  :objectFit "contain"
                  :borderRadius "4px"}
         :src (.-value params)})))

(defui render-video-preview [_params]
  ($ Box
     {:sx #js {:backgroundColor "error.main"
               :p 1
               :color "white"}}
     "TODO"))

(def field-class->render
  {"click-through-key" render-link-value})

(def placeholder-type->render
  {"audio" render-audio-preview
   "image" render-image-preview
   "video" render-video-preview})

(def field-class->header
  "Header names for Meta content fields which have a single value"
  {"meta-cta-key" "Meta CTA"
   "meta-cta-caption-key" "Meta Display Link"
   "meta-description-key" "Meta description"
   "meta-headline-key" "Meta headline"
   "meta-primary-text-key" "Meta primary text"
   "tiktok-ad-name-key" "TikTok Ad Name"
   "tiktok-ad-text-key" "TikTok Ad Text"
   "tiktok-cta-key" "TikTok CTA"
   "youtube-description-key" "YouTube description"})

(def field-class->sort-resolver
  (let [meta-resolver "ad-content-values>"
        tiktok-resolver "ad-content-values>"]
    {"content-key" "content-values>"
     "click-through-key" "click-through>"
     "meta-cta-key" meta-resolver
     "meta-cta-caption-key" meta-resolver
     "meta-description-key" meta-resolver
     "meta-headline-key" meta-resolver
     "meta-primary-text-key" meta-resolver
     "tiktok-ad-name-key" tiktok-resolver
     "tiktok-ad-text-key" tiktok-resolver
     "tiktok-cta-key" tiktok-resolver}))

(def field-class->sort+filter-field-name
  "Meta keys use a generic name for filtering/sorting instead of the field-name"
  {"meta-cta-key" "meta-cta"
   "meta-cta-caption-key" "meta-cta-caption"
   "meta-description-key" "meta-description"
   "meta-headline-key" "meta-headline"
   "meta-primary-text-key" "meta-primary-text"
   "tiktok-ad-name-key" "tiktok-ad-name"
   "tiktok-ad-text-key" "tiktok-ad-text"
   "tiktok-cta-key" "tiktok-cta"
   "youtube-description-key" "youtube-description"})

(defn default-value-getter
  [_value row ^js column ^js _apiRef]
  (gobject/get row (.-field column)))

(def row-number-column
  #js {:type "string"
       :field "sequence-number"
       :headerName  "#"
       :valueGetter default-value-getter
       :minWidth 50
       :width 50
       :sortable true
       :filterable true})

(def import-transaction-id-column
  #js {:type "string"
       :field "import-transaction-id"
       :hideable false})

(defn id-column [user-can-edit?]
  #js {:type "string"
       :field "content-id"
       :resolver "content-id"
       :headerName  "Identifier"
       :valueGetter default-value-getter
       :minWidth 200
       :flex 3
       :sortable true
       :filterable true
       :editable user-can-edit?})

(def template-dimensions-column
  #js {:type "string"
       :field "template-dimensions"
       :resolver "template-dimensions"
       :headerName  "Dimensions"
       :valueGetter default-value-getter
       :renderCell (fn [^js params]
                     (let [{:keys [adset-id]} (use-subscribe [:url-context])
                           {ad-builders :data} (use-query (q/ad-builders-upgrade-hints-query {:adset-id adset-id}))
                           ad-formats-dimensions (->> ad-builders
                                                      (mapcat :ad-formats)
                                                      (map #(select-keys % [:width :height]))
                                                      set)]
                       ($ template-dimensions-renderer
                          {:templates-dimensions (->clj (.-value params))
                           :width (.. params -colDef -computedWidth)
                           :supported-dimensions ad-formats-dimensions})))
       :minWidth 180
       :flex 1
       :sortable false
       :filterable false})

(def last-modified-column
  #js {:type "string"
       :field "updated-at"
       :headerName  "Last modified"
       :valueGetter default-value-getter
       :renderCell (fn [^js params]
                     (let [value (.-value params)]
                       ($ Tooltip
                          {:placement :right
                           :title (util/format-iso-string value)}
                          ($ Typography (util/human-readable-relative-time value)))))
       :minWidth 140
       :flex 3
       :sortable true
       :filterable false})

(defn action-column [delete-row]
  #js {:type "actions"
       :field "actions"
       :getActions (fn [^js params]
                     #js [($ GridActionsCellItem
                             {:label "Delete variant"
                              :icon ($ Icon
                                       {:sx #js {:fontSize "2rem"}}
                                       "delete")
                              :onClick #(delete-row (.-row params))})])})

(def field-order
  {"content-key" 0
   "click-through-key" 1
   "meta-cta-key" 2
   "meta-cta-caption-key" 3
   "meta-description-key" 4
   "meta-headline-key" 5
   "meta-primary-text-key" 6})

(defn content-fields [content-feed]
  (let [{:keys [field-content-mappings field-classes]} (:data content-feed)
        field-name->placeholder (fn [field-name]
                                  (-> (mc/index-by :name field-content-mappings)
                                      (get-in [field-name :placeholder])))]
    (->> field-classes
         (keep (fn [{field-name :name field-class :class}]
                 (when-not (contains? #{"identifier-key" "template-dimensions-key" "targeting-key"} field-class)
                   (let [placeholder (field-name->placeholder field-name)]
                     {:field-name field-name
                      :field-class field-class
                      :column-name (or (:name placeholder)
                                       (field-class->header field-class)
                                       field-name)
                      :placeholder placeholder}))))
         (sort-by (comp field-order :class)))))

(defn content-column [{:keys [field-name placeholder field-class column-name]} user-can-edit?]
  (let [sort+filter-field-name (get field-class->sort+filter-field-name field-class field-name)]
    #js {:type "string"
         :field column-name
         :column sort+filter-field-name
         :feedField field-name ; used for inline-editing
         :resolver (get class->filter-resolver field-class)
         :sortField  (some-> (get field-class->sort-resolver field-class)
                             (str sort+filter-field-name))
         :placeholderType (:type placeholder)
         :headerName column-name
         :valueGetter (fn [_value ^js row ^js _column ^js _apiRef]
                        (gobject/get row field-name))
         :valueSetter (fn [value ^js row ^js _column ^js _apiRef]
                        (util/merge-objects row (->js {field-name value})))
         :renderCell (or (get field-class->render field-class)
                         (get placeholder-type->render (:type placeholder)))
         :minWidth 140
         :flex 1
         :editable user-can-edit?}))
